import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../../components/Modal';
import Icon from '../../components/Icon';
import DocFileViewer from '../../components/DocFileViewer';
import { commandCenterApi, formatCurrency } from '../../utils/api';
import { formatDateShort } from '../../utils/dateFormat';
import { useIsMobile } from '../../utils/useIsMobile';
import { formatDate } from './parts';

// לטיפול → מחוקים: one soft-deleted document, read-only, inside the console
// (server/lib/softDelete.js#deletedDocumentDetail). The operator's job here is
// look → decide → restore or move on, so it opens over the list rather than in
// a new tab, and שחזר sits in its footer. Nothing here edits the document:
// until it is restored it exists only for the console.
//
// Every page is streamed through the console's own route
// (/command-center/deleted/:id/files/:n): /api/files refuses a deleted
// document's scan, and going through the server keeps the deleted-status check
// there for a Cloudinary page too. DocFileViewer decides PDF vs image from a
// path, so each page gets a stand-in name with the right extension.
//
// `row` is the list row (for the restore confirm); `reasonText` and
// `DaysLeftChip` come from QueuesPage so the two read the same.
export default function DeletedDocModal({ row, reasonText, DaysLeftChip, onRestore, restoring = false, onClose, onGone }) {
  const isMobile = useIsMobile();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  const [pageIdx, setPageIdx] = useState(0);
  const [url, setUrl] = useState('');
  const [fileError, setFileError] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const urlRef = useRef('');
  // Through a ref: the parent's list reload must not refetch the document.
  const onGoneRef = useRef(onGone);
  onGoneRef.current = onGone;

  useEffect(() => {
    let cancelled = false;
    commandCenterApi.getDeleted(row.id)
      .then(r => { if (!cancelled) setDoc(r.data); })
      .catch(err => {
        if (cancelled) return;
        if (err.response?.status === 404) {
          // Purged or restored by someone else since the list loaded.
          setError('המסמך כבר לא ברשימת המחוקים — אולי שוחזר או נמחק לצמיתות');
          onGoneRef.current?.();
        } else {
          setError('לא ניתן לטעון את המסמך — אפשר לנסות שוב');
        }
      });
    return () => { cancelled = true; };
  }, [row.id]);

  const files = (doc?.files || []).map(f => ({ ...f, file_path: `page-${f.page_number}${f.is_pdf ? '.pdf' : '.jpg'}` }));
  const pageNumber = files[Math.min(pageIdx, Math.max(0, files.length - 1))]?.page_number;

  // One page at a time, as the document page does; the previous blob is
  // revoked when the next arrives and when the viewer closes.
  useEffect(() => {
    if (pageNumber == null) return undefined;
    let cancelled = false;
    setUrl('');
    setFileError(false);
    commandCenterApi.getDeletedPage(row.id, pageNumber)
      .then(r => {
        if (cancelled) return;
        const next = URL.createObjectURL(r.data);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = next;
        setUrl(next);
      })
      .catch(() => { if (!cancelled) setFileError(true); });
    return () => { cancelled = true; };
  }, [row.id, pageNumber, attempt]);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const retryFile = useCallback(() => setAttempt(n => n + 1), []);
  // The page on screen, saved under the document's number.
  const downloadPage = () => {
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${row.doc_num || `doc-${row.id}`}${files.length > 1 ? `-page-${pageNumber}` : ''}${files.find(f => f.page_number === pageNumber)?.is_pdf ? '.pdf' : '.jpg'}`;
    a.click();
  };

  const scan = !doc ? null : files.length === 0 ? (
    <div className="cc-deleted-noscan cc-muted">לא נשמרה סריקה למסמך הזה</div>
  ) : isMobile ? (
    <button type="button" className="btn btn-secondary" onClick={() => setFullscreen(true)}>
      <Icon name="eye" size={15} /> צפה בסריקה{files.length > 1 ? ` (${files.length} עמודים)` : ''}
    </button>
  ) : (
    <DocFileViewer variant="pane" files={files} pageIdx={pageIdx} onPageChange={setPageIdx}
      url={url} onDownload={downloadPage} onMaximize={() => setFullscreen(true)}
      error={fileError} onRetry={retryFile} />
  );

  return (
    <Modal onClose={onClose} size={1100} ariaLabel="מסמך שנמחק">
      <div className="cc-deleted-head">
        <h2 className="modal-title">{row.doc_type} {row.doc_num}</h2>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="סגירה" title="סגירה">
          <Icon name="close" size={18} />
        </button>
      </div>
      <div className="cc-deleted-banner" role="status">
        <Icon name="trash" size={16} />
        <div>
          <div>
            <strong>המסמך נמחק</strong> · יימחק לצמיתות בעוד <DaysLeftChip days={row.days_left} />
          </div>
          <div className="cc-deleted-banner-meta">
            נמחק ע״י {row.deleted_by_label || row.deleted_by || '—'} ב־{formatDate(`${String(row.deleted_at).replace(' ', 'T')}Z`)}
            {' · '}סיבה: {reasonText(row)}{row.deleted_reason_note ? ` — ${row.deleted_reason_note}` : ''}
          </div>
        </div>
      </div>

      {error ? (
        <p className="cc-muted">{error}</p>
      ) : !doc ? (
        <div className="loading-container"><div className="spinner" /><span>טוען מסמך...</span></div>
      ) : (
        <div className={`cc-deleted-view${isMobile ? '' : ' dv-layout is-split'}`}>
          <div className="dv-main">
            <dl className="cc-deleted-facts">
              <div><dt>מסעדה</dt><dd>{doc.restaurant_name || '—'}</dd></div>
              <div><dt>ספק</dt><dd>{doc.supplier_name || 'לא זוהה ספק'}</dd></div>
              <div><dt>סוג מסמך</dt><dd>{doc.doc_type || '—'}</dd></div>
              <div><dt>מספר מסמך</dt><dd className="cc-num">{doc.doc_num || '—'}</dd></div>
              <div><dt>תאריך</dt><dd className="cc-num">{doc.date ? formatDateShort(doc.date) : '—'}</dd></div>
              <div><dt>סכום</dt><dd className="cc-num">{doc.total != null ? formatCurrency(doc.total) : '—'}</dd></div>
              {/* The status a restore brings back. */}
              <div><dt>סטטוס לפני המחיקה</dt><dd>{doc.status_before_delete || '—'}</dd></div>
            </dl>
            {doc.items.length > 0 ? (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>פריט</th><th>כמות</th><th>מחיר יחידה</th><th>סה״כ</th></tr>
                  </thead>
                  <tbody>
                    {doc.items.map((it, i) => (
                      <tr key={i}>
                        <td>{it.description || it.sku || '—'}</td>
                        <td className="cc-num">{it.qty ?? '—'}</td>
                        <td className="cc-num">{it.unit_price != null ? formatCurrency(it.unit_price) : '—'}</td>
                        <td className="cc-num">{it.line_total != null ? formatCurrency(it.line_total) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="cc-muted">אין פריטים במסמך</p>
            )}
            {isMobile && <div style={{ marginTop: 12 }}>{scan}</div>}
          </div>
          {!isMobile && scan}
        </div>
      )}

      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>סגור</button>
        <button type="button" className="btn btn-success" onClick={() => onRestore(row)} disabled={!!error || restoring}>
          <Icon name="undo" size={15} /> שחזר
        </button>
      </div>

      {fullscreen && files.length > 0 && (
        <DocFileViewer variant="fullscreen" files={files} pageIdx={pageIdx} onPageChange={setPageIdx}
          url={url} onClose={() => setFullscreen(false)} onDownload={downloadPage}
          error={fileError} onRetry={retryFile}
          onDock={isMobile ? undefined : () => setFullscreen(false)} />
      )}
    </Modal>
  );
}
